import { useCallback, useEffect, useRef } from 'react';
import { getProxyUrl } from '../pages/QuickGen/utils/proxyUrl';

// 原圖預抓
//
// iOS 要求 navigator.share() 在 user activation 有效期內呼叫，中間夾一次網路 fetch
// 就可能過期、丟出 NotAllowedError，使用者看到的就是點了沒反應。
// 解法是把網路延遲提前到圖片顯示時吃掉，點擊當下 blob 已經在手上。
//
// 附帶好處是點擊回應變即時，Android 上也感覺得到。

/**
 * 取得圖片 blob。遠端圖存在外部來源，直接 fetch 會被 CORS 擋掉，得經 proxy；
 * blob: / data: 則已經是本地資料，繞 proxy 反而會失敗。
 */
export async function fetchImageBlob(imageUrl: string, signal?: AbortSignal): Promise<Blob> {
  const isLocal = imageUrl.startsWith('blob:') || imageUrl.startsWith('data:');
  const res = await fetch(isLocal ? imageUrl : getProxyUrl(imageUrl), { signal });
  if (!res.ok) throw new Error(`圖片讀取失敗（HTTP ${res.status}）`);
  return res.blob();
}

/**
 * 在 imageUrl 顯示期間預抓原圖 blob。
 *
 * 回傳的 takeBlob() 是同步的 —— 這是刻意的。命中時呼叫端到 navigator.share()
 * 之間不能有任何 await，否則 user activation 會過期，預抓就白做了。
 * 未命中回 null，呼叫端自行用 fetchImageBlob() 補抓（較慢，且在 iOS 上有過期風險，
 * 但 saveImage 會接住降級成下載，不會沒反應）。
 */
export function useImageBlobPrefetch(imageUrl?: string): () => Blob | null {
  const cacheRef = useRef<{ url: string; blob: Blob } | null>(null);

  useEffect(() => {
    // 圖片換了，先前備妥的 blob 已不對應當前畫面，立刻作廢。
    // 不清的話，使用者在新圖還沒抓完時點儲存會拿到上一張 —— 這是本 hook
    // 最容易出錯也最難在 review 時看出來的地方（Lightbox 快速切圖就會踩到）。
    cacheRef.current = null;
    if (!imageUrl) return;

    const controller = new AbortController();

    void (async () => {
      try {
        const blob = await fetchImageBlob(imageUrl, controller.signal);
        // 連同來源 url 一起存。abort 的時間點與 async 回應完成的時間點不一定
        // 如預期（StrictMode 的 double-invoke 會放大這類競態），
        // 以 url 比對才是可靠的歸屬判定。
        cacheRef.current = { url: imageUrl, blob };
      } catch {
        // 預抓失敗一律靜默。使用者沒要求預抓，跟他說「預抓失敗」毫無意義；
        // 後果只是儲存時退回即時 fetch 的較慢路徑。
      }
    })();

    return () => controller.abort();
  }, [imageUrl]);

  return useCallback(() => {
    const cached = cacheRef.current;
    return cached && cached.url === imageUrl ? cached.blob : null;
  }, [imageUrl]);
}
